import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from './card'

/**
 * Narrow-screen stand-in for a table. A table cannot reflow below roughly 700px
 * without either truncating cells or scrolling sideways, so under `lg` the rows
 * become a stack of cards: one card per record, each field on its own line.
 * Render this beside a `<Table>` and let the breakpoint pick — `lg:hidden` here,
 * `hidden lg:block` on the table.
 */
function TableCardList({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="table-card-list" className={cn('flex flex-col gap-3', className)} {...props} />
}

// `title` shadows the HTML tooltip attribute; `onClick` would lose to onOpen below.
interface TableCardProps extends Omit<React.ComponentProps<'div'>, 'title' | 'onClick'> {
  /** The record's identifying field — the one that would lead the row. */
  title: React.ReactNode
  /** Row actions (edit, delete). Clicks here never reach the card's own handler. */
  actions?: React.ReactNode
  /** Opens the record. Drives the card's pointer target and the title's button. */
  onOpen?: () => void
  /** Above the fields, e.g. what the record still needs. */
  notice?: React.ReactNode
}

function TableCard({ title, actions, onOpen, notice, className, children, ...props }: TableCardProps) {
  return (
    <Card
      data-slot="table-card"
      className={cn('gap-3 py-4', onOpen && 'cursor-pointer', className)}
      {...props}
      onClick={onOpen}
    >
      {/* With a pending change's badge and buttons, which wrap, the title keeps at least half the card rather
          than shrinking to a letter a line. Actions that can't wrap keep the default columns, so they never overflow. */}
      <CardHeader className="px-4 has-data-[slot=pending-change-actions]:grid-cols-[minmax(50%,1fr)_auto]">
        {/* wrap-anywhere, not break-words: only `anywhere` shrinks min-content, so a long filename stops widening the card. */}
        <CardTitle className="flex min-w-0 items-center gap-1.5 text-sm font-semibold wrap-anywhere">
          {onOpen ? (
            // The keyboard path; the card's own handler is a pointer convenience.
            <Button
              type="button"
              variant="link"
              // `shrink` undoes Button's shrink-0, which held a long title on one line past the card's edge.
              className="text-ink hover:text-ink h-auto min-w-0 shrink justify-start p-0 text-left text-sm font-semibold whitespace-normal"
              onClick={(event) => {
                event.stopPropagation()
                onOpen()
              }}
            >
              {title}
            </Button>
          ) : (
            title
          )}
        </CardTitle>
        {actions ? (
          // The card is tappable, so actions swallow their own clicks.
          <CardAction className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {actions}
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-2 px-4">
        {notice}
        <dl className="flex flex-col gap-2">{children}</dl>
      </CardContent>
    </Card>
  )
}

interface TableCardFieldProps {
  label: string
  /** Falls back to an em dash, matching how the table renders a blank cell. */
  value?: React.ReactNode
}

function TableCardField({ label, value }: TableCardFieldProps) {
  // DriversTable coerces absent fields to '' (see its edit-form mapping), so a
  // blank string is "missing" here too — but 0 is a real value and must render.
  const isBlank = value === null || value === undefined || value === ''

  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-ink-400 shrink-0 text-xs">{label}</dt>
      <dd className="text-foreground min-w-0 text-right text-sm break-words">
        {isBlank ? <span className="text-muted-foreground">—</span> : value}
      </dd>
    </div>
  )
}

export { TableCard, TableCardField, TableCardList }
